import {
  Address_Create,
  Address_Read_Nested,
  Address_Update,
  Contact_Read_Nested,
  GeofenceType,
  NextBillionGeofence_Read,
  Site_Create,
  Site_Read,
  Site_Read_Nested,
  Site_Read_Typeahead,
  Site_Update,
  SiteState,
  SiteType,
  SiteUsage_Site_Read_Nested,
  TimeZone,
  Waypoint_Read_Nested as WayPointProto,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import { convertLength } from '@turf/helpers';
import dayjs, { Dayjs } from 'dayjs';
import type { Geometry } from 'geojson';
import { capitalize, cloneDeep, every, isEmpty } from 'lodash-es';

import { geofenceNoneOption, GeoFenceTypes } from '~constants/enums';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { CompanyBasic } from '~hooks/useCompany/models';
import { CompanyShareBasic } from '~hooks/useCompanyShares/models';
import { ContactItem } from '~hooks/useContact/models/ContactItem';
import { LatLongItem } from '~hooks/useMaps/models';
import type {
  SiteFormV2DirtyFields,
  SiteFormV2Fields,
} from '~pages/Settings/Administration/Sites/site-form-v2/types';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { isAnyFieldDirty, isFieldDirty } from '~utils/buildPatch';
import { parseFiniteNumber, splitStringBySentenceChange } from '~utils/utilFunctions';

import { BasicDepartment } from '../useDepartment/models';
import {
  generateCircleAttributes,
  generatePolygonGeoJson,
  validateAddress,
} from './utils';

export interface WayPointFormProps {
  id: string;
  type: WaypointType;
  site: SiteBasic;
  contact: ContactItem;
  ordinality: number;
}
export class WayPoint {
  public static parse(proto: WayPointProto): WayPoint {
    return new WayPoint(
      proto.id ?? '',
      proto.type ?? '',
      proto.ordinality ?? 10,
      proto.contact ? ContactItem.parse(proto.contact as Contact_Read_Nested) : null,
      proto.site ? SiteBasic.parse(proto.site) : null,
      proto.site ? Site.parseNested(proto.site) : null,
    );
  }
  public static deparse(
    proto: WayPointFormProps,
    cleanupIds?: boolean,
    replaceContact = false,
  ): any {
    const contact =
      every(proto.contact, isEmpty) || replaceContact
        ? { name: '', phone: '' }
        : ContactItem.deparse(proto.contact as unknown as ContactItem);
    const data = {
      type: proto.type,
      ...(proto.id && proto.id !== 'undefined' ? { id: proto.id } : {}),
      ordinality: proto.ordinality, // Temp until multiple waypoint will be handled in UI proto.ordinality,
      contact: Object.values(contact).some((value) => value !== undefined && value !== '')
        ? contact
        : undefined,
      site_id: proto.site.id || '',
    } as any;

    if (cleanupIds) {
      delete data.id;
    }

    return data;
  }
  public get isPickUp(): boolean {
    return this._type === WaypointType.PICKUP;
  }
  public get isDropOff(): boolean {
    return this._type === WaypointType.DROP_OFF;
  }
  public get isWeighPoint(): boolean {
    return this._type === WaypointType.WEIGH_POINT;
  }
  public get isStaging(): boolean {
    return this._type === WaypointType.STAGING;
  }
  public get id(): string {
    return this._id;
  }
  public get type(): WaypointType {
    return this._type;
  }
  public get ordinality(): number {
    return this._ordinality;
  }
  public get contact(): Nullable<ContactItem> {
    return this._contact;
  }
  public set contact(contact: Nullable<ContactItem>) {
    this._contact = cloneDeep(contact);
  }
  public get site(): Nullable<SiteBasic> {
    return this._site;
  }
  public set site(site: Nullable<SiteBasic>) {
    this._site = cloneDeep(site);
  }

  public get siteNested(): Nullable<Site> {
    return this._site_nested;
  }

  constructor(
    private _id: string,
    private _type: WaypointType,
    private _ordinality: number,
    private _contact: Nullable<ContactItem>,
    private _site: Nullable<SiteBasic>,
    private _site_nested: Nullable<Site>,
  ) {}
}

export class SiteTypeahead extends ItemNameAndId {
  public static parse(proto: Site_Read_Typeahead): SiteTypeahead {
    return new SiteTypeahead(proto.name ?? '', proto.id ?? '', proto.external_id ?? '');
  }

  public get externalId() {
    return this._external_id;
  }

  constructor(
    name: string,
    id: string,
    private _external_id: string,
  ) {
    super(name, id);
  }
}

export class SiteBasic {
  public static parse(proto: Site_Read_Nested): SiteBasic {
    return new SiteBasic(
      proto.id ?? '',
      proto.name ?? '',
      proto?.lat ?? '',
      proto?.lon ?? '',
      proto.lat && proto.lon ? `${proto.lat},${proto.lon}` : '',
      proto?.next_billion_geofence ? Geofence.parse(proto.next_billion_geofence) : null,
      proto?.full_address ?? '',
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.open_at ?? null,
      proto.close_at ?? null,
      proto.time_zone ?? null,
      proto.haul ?? true,
      proto.equipment_types
        ? proto.equipment_types.map((equipmentType) => ItemNameAndId.parse(equipmentType))
        : [],
    );
  }
  public get id(): string {
    return this._id;
  }
  public get name(): string {
    return this._name;
  }
  public get latLon(): string {
    return this._latLon;
  }
  public get lat(): Nullable<number> {
    return parseFiniteNumber(this._lat);
  }
  public get lon(): Nullable<number> {
    return parseFiniteNumber(this._lon);
  }
  public get nextBillionGeofence(): Nullable<Geofence> {
    return this._next_billion_geofence;
  }
  public get fullAddress(): string {
    return this._full_address;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get openAt() {
    return this._open_at;
  }

  public get closeAt() {
    return this._close_at;
  }

  public get timeZone() {
    return this._time_zone;
  }

  public get haul() {
    return this._haul;
  }

  public get equipmentTypes() {
    return this._equipment_types;
  }

  constructor(
    private _id: string,
    private _name: string,
    protected _lat: Nullable<string>,
    protected _lon: Nullable<string>,
    protected _latLon: string,
    protected _next_billion_geofence: Nullable<Geofence>,
    protected _full_address: string,
    protected _discarded_at: Dayjs | null,
    protected _open_at: string | null = null,
    protected _close_at: string | null = null,
    protected _time_zone: TimeZone | null = null,
    protected _haul: boolean = true,
    protected _equipment_types: ItemNameAndId[] = [],
  ) {}
}
class GeoJsonItem {
  public static parse(proto: any): GeoJsonItem {
    return new GeoJsonItem(proto.type ?? '', proto.coordinates ?? []);
  }
  public get type(): string {
    return this._type;
  }
  public get coordinates(): [] {
    return this._coordinates;
  }
  public get geometry(): Geometry {
    return {
      type: this._type,
      coordinates: this._coordinates,
    } as Geometry;
  }
  constructor(
    private _type: string,
    private _coordinates: [],
  ) {}
}
export class Geofence {
  private static _id: string;
  public static parse(proto: NextBillionGeofence_Read): Geofence {
    return new Geofence(
      proto?.id ?? '',
      proto?.geofence?.name ? proto.geofence.name : '',
      proto?.geofence?.type ? proto.geofence.type.toLowerCase() : '',
      proto.geofence?.geojson?.coordinates
        ? GeoJsonItem.parse(proto.geofence.geojson)
        : null,
      proto?.geofence?.circle_center
        ? LatLongItem.parse(proto.geofence.circle_center)
        : null,
      proto?.geofence?.circle_radius ?? 0,
      proto?.moving_geofence ?? false,
      proto?.tag ?? '',
      proto.geofence_type ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public static deparseCreate(proto: any): any {
    const geoType =
      proto?.geofenceType?.id?.toLowerCase() || GeoFenceTypes.POLYGON.toLowerCase();
    switch (geoType) {
      case GeoFenceTypes.CIRCLE.toLowerCase():
        return {
          geofence: {
            name: proto.name,
            type: geoType,
            circle_center: {
              lat: proto.circleCenter?.lat,
              lon: proto.circleCenter?.lon,
            },
            // If our company is using feet, the value in the form will be in feet, so we need to convert it to meters
            circle_radius: proto.isFeet
              ? convertLength(proto.radius, 'feet', 'meters')
              : proto.radius,
          },

          moving_geofence: false,
        };
      case GeoFenceTypes.EQUIPMENT.toLowerCase():
        return {
          geofence: {
            name: proto.name,
            type: GeoFenceTypes.CIRCLE.toLowerCase(),
            circle_center: {
              lat: proto.circleCenter?.lat,
              lon: proto.circleCenter?.lon,
            },
            // If our company is using feet, the value in the form will be in feet, so we need to convert it to meters
            circle_radius: proto.isFeet
              ? convertLength(proto.radius, 'feet', 'meters')
              : proto.radius,
          },

          moving_geofence: true,
        };
      case GeoFenceTypes.POLYGON.toLowerCase():
        return {
          geofence: {
            name: proto.name,
            type: geoType,
            geojson: {
              type: capitalize(GeoFenceTypes.POLYGON),
              coordinates:
                proto.geoFence?.geojson?.coordinates ||
                proto.geoFence?.geometry?.coordinates,
            },
          },

          moving_geofence: false,
        };
      case geofenceNoneOption.id:
        return proto?.geoFenceInitialId
          ? {
              id: proto.geoFenceInitialId,
              _destroy: 1,
            }
          : undefined;
    }
  }
  public static deparseUpdate(proto: any): any {
    const geoType = proto?.geofenceType?.id.toLowerCase();

    if (!geoType) return undefined;

    const commonFields = {
      id: proto?.geoFenceInitialId?.length ? proto.geoFenceInitialId : undefined,
      name: proto.name,
      type: geoType,
      tag: proto.tag ? proto.tag : undefined,
    };

    switch (geoType) {
      case GeoFenceTypes.CIRCLE.toLowerCase():
        return {
          geofence: {
            ...commonFields,
            circle_center: {
              lat: proto.circleCenter?.lat,
              lon: proto.circleCenter?.lon,
            },
            // If our company is using feet, the value in the form will be in feet, so we need to convert it to meters
            circle_radius: proto.isFeet
              ? convertLength(proto.radius, 'feet', 'meters')
              : proto.radius,
          },
          moving_geofence: false,
        };

      case GeoFenceTypes.EQUIPMENT.toLowerCase():
        return {
          geofence: {
            ...commonFields,
            circle_center: {
              lat: proto.circleCenter?.lat,
              lon: proto.circleCenter?.lon,
            },
            // If our company is using feet, the value in the form will be in feet, so we need to convert it to meters
            circle_radius: proto.isFeet
              ? convertLength(proto.radius, 'feet', 'meters')
              : proto.radius,
            type: GeoFenceTypes.CIRCLE.toLowerCase(),
          },
          moving_geofence: true,
        };

      case GeoFenceTypes.POLYGON.toLowerCase():
        return {
          geofence: {
            ...commonFields,
            geojson: {
              type: capitalize(GeoFenceTypes.POLYGON),
              coordinates:
                proto.geoFence?.geojson?.coordinates ||
                proto.geoFence?.geometry?.coordinates,
            },
          },
          moving_geofence: false,
        };

      case geofenceNoneOption.id:
        return proto?.geoFenceInitialId
          ? {
              id: proto.geoFenceInitialId,
              _destroy: 1,
            }
          : undefined;

      default:
        return undefined;
    }
  }
  public static deparseAdditionalGeoFence(proto: any): any {
    const geoType = proto?.type?.id.toLowerCase();

    const commonFields = {
      name: proto.name,
      type: geoType,
    };

    switch (geoType) {
      case GeoFenceTypes.CIRCLE.toLowerCase():
        return {
          geofence: {
            ...commonFields,
            circle_center: {
              lat: proto.circleCenter?.lat,
              lon: proto.circleCenter?.lon,
            },
            // If our company is using feet, the value in the form will be in feet, so we need to convert it to meters
            circle_radius: proto.isFeet
              ? convertLength(proto.radius, 'feet', 'meters')
              : proto.radius,
          },
          tag: proto.tag.id,

          moving_geofence: false,
        };

      case GeoFenceTypes.EQUIPMENT.toLowerCase():
        return {
          geofence: {
            ...commonFields,
            circle_center: {
              lat: proto.circleCenter?.lat,
              lon: proto.circleCenter?.lon,
            },
            // If our company is using feet, the value in the form will be in feet, so we need to convert it to meters
            circle_radius: proto.isFeet
              ? convertLength(proto.radius, 'feet', 'meters')
              : proto.radius,
            type: GeoFenceTypes.CIRCLE.toLowerCase(),
          },
          tag: proto.tag.id,

          moving_geofence: true,
        };

      case GeoFenceTypes.POLYGON.toLowerCase():
        return {
          geofence: {
            ...commonFields,
            geojson: {
              type: capitalize(GeoFenceTypes.POLYGON),
              coordinates: proto.geojson?.coordinates || proto.geometry?.coordinates,
            },
          },
          tag: proto.tag.id,
          moving_geofence: false,
        };

      default:
        return undefined;
    }
  }

  public get id(): string {
    return this._id;
  }
  public get name(): string {
    return this._name;
  }
  public get type(): string {
    return this._type.toLowerCase();
  }
  public get geojson(): Nullable<GeoJsonItem> {
    return this._geojson;
  }
  public get circleCenter(): Nullable<LatLongItem> {
    return this._circle_center;
  }
  public get circleRadius(): number {
    return this._circle_radius;
  }
  public get movingGeofence(): boolean {
    return this._moving_geofence;
  }
  public get tag(): string {
    return this._tag;
  }
  public get geofenceType(): Nullable<GeofenceType> {
    return this._geofenceType;
  }
  public get discardedAt() {
    return this._discarded_at;
  }
  constructor(
    private _id: string,
    private _name: string,
    private _type: string,
    private _geojson: Nullable<GeoJsonItem>,
    private _circle_center: Nullable<LatLongItem>,
    private _circle_radius: number,
    private _moving_geofence: boolean,
    private _tag: string,
    private _geofenceType: Nullable<GeofenceType>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}
}
export class Site extends SiteBasic {
  public static parse(proto: Site_Read): Site {
    return new Site(
      proto.company ? CompanyBasic.parse(proto.company) : null,
      AddressItem.parse(
        proto.address ?? ({} as Address_Read_Nested),
        proto.full_address ?? '',
      ),
      proto.id ?? '',
      proto.name ?? '',
      proto.lat ?? '',
      proto.lon ?? '',
      proto.site_type ?? null,
      proto.state ?? SiteState.INACTIVE,
      proto.notes ?? '',
      proto?.next_billion_geofence ? Geofence.parse(proto.next_billion_geofence) : null,
      proto.external_id ?? '',
      proto.lat && proto.lon ? `${proto.lat},${proto.lon}` : '',
      proto.routable ?? false,
      proto.moving_site ?? null,
      proto?.full_address ?? '',
      proto.department ? BasicDepartment.parse(proto.department) : null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.created_at ? dayjs(proto.created_at) : null,
      proto.updated_at ? dayjs(proto.updated_at) : null,
      proto.contacts ? proto.contacts.map((contact) => ContactItem.parse(contact)) : [],
      proto.company_share ? CompanyShareBasic.parse(proto.company_share) : null,
      proto.open_at ?? null,
      proto.close_at ?? null,
      proto.time_zone ?? null,
      proto.haul ?? true,
      proto.equipment_types
        ? proto.equipment_types.map((equipmentType) => ItemNameAndId.parse(equipmentType))
        : [],
    );
  }

  public static parseNested(
    proto: Site_Read_Nested & Partial<Pick<SiteUsage_Site_Read_Nested, 'moving_site'>>,
  ): Site {
    return new Site(
      null,
      proto.address ? AddressItem.parse(proto.address, proto.full_address ?? '') : null,
      proto.id ?? '',
      proto.name ?? '',
      proto.lat ?? '',
      proto.lon ?? '',
      proto.site_type ?? null,
      SiteState.ACTIVE,
      '',
      proto?.next_billion_geofence ? Geofence.parse(proto.next_billion_geofence) : null,
      proto.external_id ?? '',
      proto.lat && proto.lon ? `${proto.lat},${proto.lon}` : '',
      proto.routable ?? false,
      proto.moving_site ?? null,
      proto?.full_address ?? '',
      null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      null,
      null,
      [],
      null,
      proto.open_at ?? null,
      proto.close_at ?? null,
      proto.time_zone ?? null,
      proto.haul ?? true,
      proto.equipment_types
        ? proto.equipment_types.map((equipmentType) => ItemNameAndId.parse(equipmentType))
        : [],
    );
  }

  public static deparse(proto: any): Site_Create | Site_Update {
    const lat = proto.latLon?.split(',')?.[0] ? proto.latLon.split(',')[0] : '';
    const lon = proto.latLon?.split(',')?.[1] ? proto.latLon.split(',')[1] : '';

    const isValidAddress = validateAddress(proto.address);
    const address = isValidAddress
      ? (AddressItem.deparse(proto.address) as Address_Create)
      : proto?.address?.streetAddress || undefined;

    const data = {
      name: proto.name,
      company_id: proto.company?.id || undefined,
      // If one of values is empty set to undefined,as its all or none required rule
      ...(isValidAddress ? { address } : { full_address: address }),
      lat,
      lon,
      notes: proto.notes ?? '',
      external_id: proto.externalId,
      next_billion_geofence: proto?.geoFence?.name?.length
        ? Geofence.deparseCreate(proto)
        : undefined,
      department_id: proto.department?.id || null,
    } as any;

    if (proto.siteType) {
      data.site_type = proto.siteType.replace(/\s/g, '');
    }

    if (proto.geofenceType?.id === GeoFenceTypes.EQUIPMENT.toLowerCase()) {
      data.moving_site = {
        id: proto.equipment.id,
        equipment_id: proto.equipment.id,
      };
    }

    return data;
  }

  public static deparseCreate(proto: any): Site_Create {
    const lat = proto.latLon?.split(',')?.[0] ? proto.latLon.split(',')[0] : '';
    const lon = proto.latLon?.split(',')?.[1] ? proto.latLon.split(',')[1] : '';

    const isValidAddress = validateAddress(proto.address);
    const address = isValidAddress
      ? (AddressItem.deparseCreate(proto.address) as Address_Create)
      : proto?.address?.streetAddress || undefined;

    const data = {
      name: proto.name,
      // If one of values is empty set to undefined,as its all or none required rule
      ...(isValidAddress ? { address } : { full_address: address }),
      lat,
      lon,
      notes: proto.notes ?? '',
      external_id: proto.externalId,
      next_billion_geofence: proto?.geoFence?.name?.length
        ? Geofence.deparseCreate(proto)
        : undefined,
      department_id: proto.department?.id || null,
      open_at: proto.openAt ?? null,
      close_at: proto.closeAt ?? null,
      ...(proto.timeZone ? { time_zone: proto.timeZone } : {}),
      equipment_type_ids: (proto.equipmentTypes ?? []).map(
        (equipmentType: { value: string }) => equipmentType.value,
      ),
    } as any;

    if (proto.siteType) {
      data.site_type = proto.siteType.replace(/\s/g, '');
    }

    if (proto.geofenceType?.id === GeoFenceTypes.EQUIPMENT.toLowerCase()) {
      data.moving_site = {
        id: proto.equipment.id,
        equipment_id: proto.equipment.id,
      };
    }

    return data;
  }

  public static deparseUpdate(proto: any): Site_Create | Site_Update {
    const lat = proto.latLon?.split(',')?.[0] ? proto.latLon.split(',')[0] : '';
    const lon = proto.latLon?.split(',')?.[1] ? proto.latLon.split(',')[1] : '';

    const isValidAddress = validateAddress(proto.address);
    const address = isValidAddress
      ? (AddressItem.deparse(proto.address) as Address_Update)
      : proto?.address?.streetAddress || undefined;

    const data = {
      name: proto.name,
      // If one of values is empty set to undefined,as its all or none required rule
      ...(isValidAddress ? { address } : { full_address: address }),
      lat,
      lon,
      notes: proto.notes,
      external_id: proto.externalId,
      next_billion_geofence: Geofence.deparseUpdate(proto),
      department_id: proto.department?.id || null,
    } as any;

    if (proto.siteType) {
      data.site_type = proto.siteType.replace(/\s/g, '');
    }
    if (proto.geofenceType?.id === GeoFenceTypes.EQUIPMENT.toLowerCase()) {
      data.moving_site = {
        id: proto.equipment.id,
        equipment_id: proto.equipment.id,
      };
    }
    return data;
  }

  /**
   * JSON Merge Patch–style deparse for the Settings → Sites V2 form's update
   * (PATCH) flow. Only emits keys whose underlying form field was touched
   * according to RHF's `dirtyFields`; untouched keys are omitted so the BE
   * leaves the server-side value alone. Bakes in the geofence shaping,
   * `moving_site`, and address-`id` cleanup that previously lived in the
   * useSiteFormV2 hook on top of `deparseUpdate`.
   */
  public static deparseSiteFormV2Update(
    proto: SiteFormV2Fields,
    dirtyFields: SiteFormV2DirtyFields | undefined,
  ): Site_Update {
    const patch: Record<string, unknown> = {};

    if (isFieldDirty(dirtyFields, 'name')) {
      patch.name = proto.name;
    }
    if (isFieldDirty(dirtyFields, 'notes')) {
      patch.notes = proto.notes;
    }
    if (isFieldDirty(dirtyFields, 'externalId')) {
      patch.external_id = proto.externalId;
    }
    if (isFieldDirty(dirtyFields, 'department')) {
      patch.department_id = proto.department?.id || null;
    }
    if (isFieldDirty(dirtyFields, 'siteType')) {
      patch.site_type = proto.siteType
        ? proto.siteType.replace(/\s/g, '')
        : proto.siteType;
    }
    if (isFieldDirty(dirtyFields, 'latLon')) {
      patch.lat = proto.latLon?.split(',')?.[0] ? proto.latLon.split(',')[0] : '';
      patch.lon = proto.latLon?.split(',')?.[1] ? proto.latLon.split(',')[1] : '';
    }
    if (isFieldDirty(dirtyFields, 'openAt')) {
      patch.open_at = proto.openAt;
    }
    if (isFieldDirty(dirtyFields, 'closeAt')) {
      patch.close_at = proto.closeAt;
    }
    if (isFieldDirty(dirtyFields, 'timeZone')) {
      patch.time_zone = proto.timeZone;
    }
    if (isFieldDirty(dirtyFields, 'equipmentTypes')) {
      patch.equipment_type_ids = proto.equipmentTypes.map(
        (equipmentType) => equipmentType.value,
      );
    }

    if (isFieldDirty(dirtyFields, 'address')) {
      const isValid = validateAddress(proto.address);
      if (isValid) {
        const address = AddressItem.deparse(proto.address) as Address_Update;
        // Do not include an empty address.id in the update request.
        if (address && typeof address === 'object' && !(address as { id?: string }).id) {
          (address as { id?: string }).id = undefined;
        }
        patch.address = address;
      } else {
        patch.full_address = proto?.address?.streetAddress || undefined;
      }
    }

    // Geofence "field group": emit `next_billion_geofence` if any geofence
    // input was touched. We always send the full geofence sub-object since the
    // BE's geofence shape varies by type (Polygon / Circle / Equipment / None).
    // The geofence's own `name` subfield mirrors the site `name`, but renaming
    // the site is not the user's intent to rebuild the geofence — the BE keeps
    // them in sync server-side.
    const geofenceDirty = isAnyFieldDirty(dirtyFields, [
      'geofenceType',
      'geofenceId',
      'radius',
      'circleCenter',
      'drawingCoordinates',
    ]);
    if (geofenceDirty) {
      const [latParsed, lonParsed] = (proto.latLon ?? '')
        .split(',')
        .map((v: string) => v.trim())
        .map((v: string) => parseFloat(v));
      const primaryCircleCenter = proto.circleCenter
        ? { lat: proto.circleCenter.lat, lon: proto.circleCenter.lng }
        : { lat: latParsed, lon: lonParsed };

      if (proto.drawingCoordinates?.length) {
        patch.next_billion_geofence = {
          geofence: {
            name: proto.name ?? '',
            type: GeofenceType.POLYGON,
            geojson: generatePolygonGeoJson(proto.drawingCoordinates),
          },
          moving_geofence: false,
        };
      } else if (proto.geofenceType?.name === 'Circle') {
        patch.next_billion_geofence = {
          geofence: {
            name: proto.name ?? '',
            type: GeofenceType.CIRCLE,
            ...generateCircleAttributes({
              center: primaryCircleCenter,
              radiusInFeet: Number(proto.radius),
            }),
          },
          moving_geofence: false,
        };
      } else if (proto.geofenceType?.name === 'Equipment') {
        patch.next_billion_geofence = {
          geofence: {
            name: proto.name ?? '',
            type: GeofenceType.CIRCLE,
            ...generateCircleAttributes({
              center: primaryCircleCenter,
              radiusInFeet: Number(proto.radius),
            }),
          },
          moving_geofence: true,
        };
      } else if (proto.geofenceType?.name === 'None') {
        patch.next_billion_geofence = proto.geofenceId
          ? {
              id: proto.geofenceId,
              _destroy: 1,
            }
          : undefined;
      }
    }

    // Moving site is only meaningful when the geofence is Equipment-tracked.
    // Emit when any equipment-related field is dirty (or the geofence flipped
    // to Equipment on this edit).
    const movingSiteDirty = isAnyFieldDirty(dirtyFields, [
      'equipment',
      'isPaverTracker',
      'remoteSyncEnabled',
      'geofenceType',
    ]);
    if (movingSiteDirty && proto.geofenceType?.name === 'Equipment') {
      patch.moving_site = {
        equipment_id: proto.equipment?.id,
        ...(proto.isPaverTracker && {
          remote_sync_enabled: proto.remoteSyncEnabled,
        }),
      };
    }

    return patch as Site_Update;
  }

  public get company(): Nullable<CompanyBasic> {
    return this._company;
  }
  public get companyName(): string {
    return this._company?.legalName || '';
  }

  public get siteType(): string {
    return this._site_type ? splitStringBySentenceChange(this._site_type) : '';
  }

  public get address(): Nullable<AddressItem> {
    return this._address;
  }

  public get isActive(): boolean {
    return this._state === SiteState.ACTIVE;
  }

  public get lng(): Nullable<number> {
    return this._lon ? parseFloat(this._lon) : null;
  }

  public get notes(): string {
    return this._notes;
  }
  public get externalId(): string {
    return this._external_id;
  }
  public get routable(): boolean {
    return this._routable;
  }

  public get movingSite(): Nullable<{
    id: string;
    equipment_id: string;
    type?: string;
    remote_sync_enabled?: boolean;
  }> {
    return this._moving_site;
  }

  public get fullAddress(): string {
    return this._full_address;
  }

  public get department(): Nullable<BasicDepartment> {
    return this._department;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get contacts() {
    return this._contacts;
  }

  public get companyShare() {
    return this._companyShare;
  }

  constructor(
    private _company: Nullable<CompanyBasic>,
    private _address: Nullable<AddressItem>,
    _id: string,
    _name: string,
    _lat: Nullable<string>,
    _lon: Nullable<string>,
    private _site_type: Nullable<SiteType>,
    private _state: SiteState,
    private _notes: string,
    _next_billion_geofence: Nullable<Geofence>,
    private _external_id: string,
    _latLon: string,
    private _routable: boolean,
    private _moving_site: Nullable<{
      id: string;
      equipment_id: string;
      type?: string;
      remote_sync_enabled?: boolean;
    }>,
    _full_address: string,
    private _department: Nullable<BasicDepartment>,
    _discarded_at: Dayjs | null,
    private _created_at: Dayjs | null,
    private _updated_at: Dayjs | null,
    private _contacts: ContactItem[],
    private _companyShare: Nullable<CompanyShareBasic>,
    _open_at: string | null = null,
    _close_at: string | null = null,
    _time_zone: TimeZone | null = null,
    _haul: boolean = true,
    _equipment_types: ItemNameAndId[] = [],
  ) {
    super(
      _id,
      _name,
      _lat,
      _lon,
      _latLon,
      _next_billion_geofence,
      _full_address,
      _discarded_at,
      _open_at,
      _close_at,
      _time_zone,
      _haul,
      _equipment_types,
    );
    this._created_at = _created_at;
    this._updated_at = _updated_at;
  }
}
